export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  accountScoped?: boolean
  query?: Record<string, string | number | boolean | null | undefined>
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type TokenProvider = () => Promise<string | null>
type AccountProvider = () => string | null
type RefreshProvider = () => Promise<string | null>
type AcceptanceHandler = () => void | Promise<void>

export class ApiClient {
  private tokenProvider: TokenProvider = async () => null
  private accountProvider: AccountProvider = () => null
  private refreshProvider: RefreshProvider = async () => null
  private acceptanceHandler: AcceptanceHandler = () => undefined

  constructor(private readonly baseUrl: string) {}

  configure(options: {
    getToken: TokenProvider
    getAccountId: AccountProvider
    refreshToken: RefreshProvider
    onAcceptanceRequired?: AcceptanceHandler
  }) {
    this.tokenProvider = options.getToken
    this.accountProvider = options.getAccountId
    this.refreshProvider = options.refreshToken
    this.acceptanceHandler = options.onAcceptanceRequired ?? (() => undefined)
  }

  get<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>(path, { ...options, method: 'GET' })
  }

  post<T>(path: string, body?: unknown, options: ApiRequestOptions = {}) {
    return this.request<T>(path, { ...options, method: 'POST', body })
  }

  put<T>(path: string, body?: unknown, options: ApiRequestOptions = {}) {
    return this.request<T>(path, { ...options, method: 'PUT', body })
  }

  patch<T>(path: string, body?: unknown, options: ApiRequestOptions = {}) {
    return this.request<T>(path, { ...options, method: 'PATCH', body })
  }

  delete<T>(path: string, options: ApiRequestOptions = {}) {
    return this.request<T>(path, { ...options, method: 'DELETE' })
  }

  async request<T>(path: string, options: ApiRequestOptions = {}, retried = false): Promise<T> {
    const accountScoped = options.accountScoped ?? true
    const token = await this.tokenProvider()
    const accountId = this.accountProvider()
    if (accountScoped && !accountId) {
      throw new ApiError(400, 'ACCOUNT_REQUIRED', 'Select an account before continuing.')
    }

    const url = new URL(path, `${this.baseUrl.replace(/\/$/, '')}/`)
    Object.entries(options.query ?? {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
    })

    const headers = new Headers(options.headers)
    headers.set('Accept', 'application/json')
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (accountScoped && accountId) headers.set('X-Account-ID', accountId)
    if (options.body !== undefined) headers.set('Content-Type', 'application/json')

    let response: Response
    try {
      response = await fetch(url, {
        ...options,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        headers,
      })
    } catch (cause) {
      throw new ApiError(0, 'NETWORK_ERROR', 'The server could not be reached. Check your connection and try again.', undefined, cause)
    }

    if (response.status === 401 && !retried) {
      const refreshed = await this.refreshProvider()
      if (refreshed) return this.request<T>(path, options, true)
    }

    if (!response.ok) {
      const requestId = response.headers.get('x-request-id') ?? undefined
      let payload: {
        error?: { code?: string; message?: string; details?: unknown; request_id?: string }
        code?: string
        message?: string
        detail?: unknown
      } = {}
      try {
        payload = await response.json()
      } catch {
        payload.message = response.statusText
      }
      const nested = payload.error
      const code = nested?.code ?? payload.code ?? `HTTP_${response.status}`
      const resolvedRequestId = nested?.request_id ?? requestId
      if (response.status === 403 && (code === 'ACCEPTANCE_REQUIRED' || code === 'TERMS_ACCEPTANCE_REQUIRED')) {
        await this.acceptanceHandler()
      }
      throw new ApiError(
        response.status,
        code,
        nested?.message ?? payload.message ?? validationMessage(payload.detail) ?? 'The request could not be completed.',
        resolvedRequestId,
        nested?.details ?? payload.detail,
      )
    }

    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }
}

export const api = new ApiClient(import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8000')

function validationMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string' && detail.trim()) return detail
  if (!Array.isArray(detail)) return undefined
  const parts = detail.flatMap((item) => {
    if (!item || typeof item !== 'object' || !('msg' in item)) return []
    const message = String((item as { msg: unknown }).msg)
    const loc = Array.isArray((item as { loc?: unknown }).loc)
      ? (item as { loc: unknown[] }).loc.filter((part) => part !== 'body').join('.')
      : ''
    return [loc ? `${loc}: ${message}` : message]
  })
  return parts.length ? parts.join('; ') : undefined
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId ? `${error.message} Request ID: ${error.requestId}` : error.message
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.'
}
